import { Children, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@parrot-software/design-system';

const TAB_KEYS = ['subscriptions', 'paymentHistory'] as const;
type TabKey = typeof TAB_KEYS[number];

const isValidTab = (value: string | null): value is TabKey => TAB_KEYS.includes(value as TabKey);

interface SubscriptionTabsProps {
  children: [React.ReactNode, React.ReactNode];
}

/**
 * Tabs wrapper for the subscription domain tabs.
 * Uses the `?tab=` query parameter as the single source of truth so the
 * active tab survives page refreshes and supports deep-linking.
 *
 * @param {Object} props - Component properties
 * @param {[React.ReactNode, React.ReactNode]} props.children - Tuple: [SubscriptionsContent, PaymentHistoryContent]
 * @returns {React.ReactElement} Subscription tabs component
 */
export const SubscriptionTabs = ({ children }: SubscriptionTabsProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const { search } = useLocation();

  const tabParam = new URLSearchParams(search).get('tab');
  const isTabValid = isValidTab(tabParam);
  const activeTab: TabKey = isTabValid ? tabParam : TAB_KEYS[0];

  useEffect(() => {
    if (tabParam && !isTabValid) {
      const params = new URLSearchParams(search);
      params.delete('tab');
      const qs = params.toString();
      history.replace({ search: qs ? `?${qs}` : '' });
    }
  }, [tabParam, isTabValid, search]);

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(search);
    if (value === TAB_KEYS[0]) {
      params.delete('tab');
    } else {
      params.set('tab', value);
    }
    const qs = params.toString();
    history.replace({ search: qs ? `?${qs}` : '' });
  };

  const childArray = Children.toArray(children);

  return (
    <div className="px-6 pt-2 pb-6">
      <Tabs onValueChange={handleTabChange} value={activeTab}>
        <TabsList>
          {TAB_KEYS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {TAB_KEYS.map((key, index) => (
          <TabsContent className="mt-4" key={key} value={key}>
            {childArray[index]}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
